import axios from 'axios';

import {
  CompanyEmailPreviewVariant,
  CompanyEmailTemplatePreviewData,
  CompanyEmailTemplateData,
  CompanyEmailWorkflow
} from 'src/contracts/companyProfile';
import { getAdminWorkspaceContext } from 'src/queries/tenant/api';
import { readStoredWorkspaceIdentity } from 'src/workspaces/AdminWorkspaceProvider';
import {
  companyTenantApiPath,
  platformTenantPath,
  TenantRouteScopeError
} from 'src/workspaces/tenantWorkspace';

const positiveCompanyId = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    throw new TenantRouteScopeError();
  }
  return parsed;
};

export const reusableEmailTemplatePath = async (companyIdValue: unknown) => {
  const companyId = positiveCompanyId(companyIdValue);
  const identity = readStoredWorkspaceIdentity();
  if (identity.kind !== 'realm') throw new TenantRouteScopeError();
  if (identity.principal.role === 'SUPER_ADMIN') {
    return platformTenantPath(companyId, 'profile/email-templates');
  }
  const workspace = await getAdminWorkspaceContext();
  if (
    workspace.actorId !== identity.principal.id ||
    workspace.company.id !== companyId
  ) {
    throw new TenantRouteScopeError();
  }
  return companyTenantApiPath(workspace, companyId, 'profile/email-templates');
};

export async function getReusableCompanyEmailTemplates(
  companyId: unknown,
  workflow: CompanyEmailWorkflow
): Promise<CompanyEmailTemplateData[]> {
  const path = await reusableEmailTemplatePath(companyId);
  const response = await axios.get(path);
  const templates = response.data?.data?.templates;
  if (!Array.isArray(templates)) {
    throw new Error('Company email template response is invalid');
  }
  return (templates as CompanyEmailTemplateData[])
    .filter(
      (template) =>
        template.workflow === workflow && template.status === 'ACTIVE'
    )
    .sort((left, right) => {
      if (left.isDefault !== right.isDefault) return left.isDefault ? -1 : 1;
      if (left.ownerType !== right.ownerType) {
        return left.ownerType === 'COMPANY' ? -1 : 1;
      }
      return right.updatedAt.localeCompare(left.updatedAt);
    });
}

export async function previewReusableCompanyEmailTemplate(
  companyId: unknown,
  input: {
    variant: CompanyEmailPreviewVariant;
    pendingActuals?: Record<string, unknown>;
    pendingInvoice?: Record<string, unknown>;
    pendingQuote?: Record<string, unknown>;
    subject: string;
    body: string;
    event: {
      id?: string | number;
      name?: string;
      venue?: string;
      address?: string;
      depositPaid?: number;
      billTo?: string;
      startDate?: string;
      endDate?: string;
    };
  }
): Promise<CompanyEmailTemplatePreviewData> {
  const path = await reusableEmailTemplatePath(companyId);
  const response = await axios.post(`${path}/preview`, input);
  return response.data.data as CompanyEmailTemplatePreviewData;
}
